/**
 * TimerControls Component
 * 
 * Timer-Anzeige und Steuerung für Live-Quiz.
 * Zeigt verbleibende Zeit an mit Start/Pause/Stop-Buttons.
 */

import React from 'react';

/**
 * Get timer color based on remaining time
 */
const getTimerColor = (timeRemaining) => {
  if (!timeRemaining) return 'text-gray-700 dark:text-white';
  if (timeRemaining <= 5) return 'text-danger-600 dark:text-danger-400';
  if (timeRemaining <= 10) return 'text-warning-600 dark:text-warning-400';
  return 'text-success-600 dark:text-success-400';
};

/**
 * @param {Object} props
 * @param {number|null} props.timeRemaining - Remaining time in seconds
 * @param {boolean} props.timerActive - Whether timer is running
 * @param {boolean} props.timerPaused - Whether timer is paused
 * @param {Function} props.onStart - Start timer handler
 * @param {Function} props.onPause - Pause timer handler
 * @param {Function} props.onResume - Resume timer handler
 * @param {Function} props.onStop - Stop timer handler
 * @param {Function} props.onAddTime - Add bonus time handler
 */
export function TimerControls({ 
  timeRemaining,
  timerActive,
  timerPaused,
  onStart,
  onPause,
  onResume,
  onStop,
  onAddTime
}) {
  const timerColor = getTimerColor(timeRemaining);
  
  // Format time display
  const formatTime = (seconds) => {
    if (seconds === null) return '--:--';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-4 mb-6 flex items-center justify-center gap-6 shadow border border-gray-200 dark:border-gray-700">
      {/* Timer Display */}
      <div className={`flex items-center gap-3 text-5xl font-mono font-bold ${timerColor} ${timerPaused ? 'opacity-60' : ''}`}>
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        {formatTime(timeRemaining)}
        {timerPaused && (
          <svg className="w-8 h-8 ml-2" fill="currentColor" viewBox="0 0 24 24">
            <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
          </svg>
        )}
      </div>
      
      {/* Timer Buttons */}
      <div className="flex gap-2">
        {!timerActive && !timerPaused ? (
          <button
            onClick={onStart}
            className="px-4 py-2 bg-success-600 hover:bg-success-700 text-white rounded-lg font-medium theme-transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
            Timer starten
          </button>
        ) : timerPaused ? (
          <button
            onClick={onResume}
            className="px-4 py-2 bg-success-600 hover:bg-success-700 text-white rounded-lg font-medium theme-transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M8 5v14l11-7z" />
            </svg>
            Fortsetzen
          </button>
        ) : (
          <button
            onClick={onPause}
            className="px-4 py-2 bg-warning-600 hover:bg-warning-700 text-white rounded-lg font-medium theme-transition flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z" />
            </svg>
            Pause
          </button>
        )}
        
        {/* Stop Button (only when active or paused) */}
        {(timerActive || timerPaused) && (
          <button
            onClick={onStop}
            className="px-4 py-2 bg-danger-600 hover:bg-danger-700 text-white rounded-lg font-medium theme-transition flex items-center gap-1"
          >
            ⏹️ Stop
          </button>
        )}
        
        {/* Add Time Button */}
        <button
          onClick={onAddTime}
          disabled={timeRemaining === null && !timerPaused}
          className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:text-gray-500 text-white rounded-lg font-medium theme-transition"
        >
          +20s
        </button>
      </div>
    </div>
  );
}

export default TimerControls;
